import type * as React from "react";

import { PatientSprite, type PatientSpriteProps } from "./PatientSprite";
import type { SpriteTraits } from "./sprites";
import { cn } from "../lib/cn";

export type PatientHeaderSize = "display" | "card" | "workstation";

const sizeChrome: Record<
  PatientHeaderSize,
  {
    sprite: PatientSpriteProps["size"];
    row: string;
    name: string;
    meta: string;
  }
> = {
  display: {
    sprite: 56,
    row: "items-start gap-[18px]",
    name: "text-[2.5rem] leading-[1.05] tracking-[-0.02em]",
    meta: "mt-1.5 text-lg",
  },
  card: {
    sprite: 44,
    row: "items-start gap-[18px]",
    name: "text-3xl leading-[1.05] tracking-[-0.02em]",
    meta: "mt-1.5 text-lg",
  },
  workstation: {
    sprite: 44,
    row: "items-center gap-[18px]",
    name: "text-3xl leading-none tracking-[-0.02em]",
    meta: "pt-1.5 text-sm",
  },
};

export interface PatientHeaderProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  name: React.ReactNode;
  meta?: React.ReactNode;
  sprite?: SpriteTraits | null;
  size?: PatientHeaderSize;
  trailing?: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
}

export function PatientHeader({
  name,
  meta,
  sprite,
  size = "display",
  trailing,
  className,
  ...rest
}: PatientHeaderProps) {
  const chrome = sizeChrome[size];
  const inline = size === "workstation";

  return (
    <header
      className={cn("flex items-start justify-between gap-6", className)}
      {...rest}
    >
      <div className={cn("flex min-w-0", chrome.row)}>
        <PatientSprite
          sprite={sprite}
          size={chrome.sprite}
          className="mt-0.5"
        />
        <div
          className={cn(
            "min-w-0",
            inline && "flex flex-wrap items-baseline gap-x-[18px]",
          )}
        >
          <h1 className={cn("font-display font-medium text-ink", chrome.name)}>
            {name}
          </h1>
          {meta != null && (
            <p className={cn("font-body text-annotation", chrome.meta)}>
              {meta}
            </p>
          )}
        </div>
      </div>
      {trailing != null && <div className="shrink-0 pt-2.5">{trailing}</div>}
    </header>
  );
}
